/**
 * Admin Security — Multi-Factor Authentication (TOTP) self-enrollment.
 * Talks to /api/admin/mfa/* (status / setup / enable / disable). Self-service: every admin
 * manages their own MFA. Backend stores secret/backup codes on admin_users.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Alert, Chip, CircularProgress,
  Stepper, Step, StepLabel, Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';

type Status = { enabled: boolean; enabledAt: string | null };

const post = async (path: string, body?: unknown) => {
  const res = await fetch(`/api/admin/mfa/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || data.message || `Request failed (${res.status})`);
  return data;
};

export const MfaSettings: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // enrollment flow
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  // disable flow
  const [disableCode, setDisableCode] = useState('');

  const loadStatus = async () => {
    try {
      const res = await fetch('/api/admin/mfa/status', { credentials: 'include' });
      const data = await res.json();
      setStatus({ enabled: !!data.enabled, enabledAt: data.enabledAt || null });
    } catch {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startEnroll = async () => {
    setError(null); setBusy(true);
    try {
      const data = await post('setup');
      setSecret(data.secret);
      setQr(data.qrCode);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Setup failed');
    } finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    if (!secret || !code) return;
    setError(null); setBusy(true);
    try {
      const data = await post('enable', { secret, token: code.trim() });
      setBackupCodes(data.backupCodes || []);
      setSecret(null); setQr(null); setCode('');
      await loadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!disableCode) return;
    setError(null); setBusy(true);
    try {
      await post('disable', { token: disableCode.trim() });
      setDisableCode('');
      await loadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disable failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const step = backupCodes ? 2 : qr ? 1 : 0;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SecurityIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Security — Two-Factor Authentication</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Protect your admin account with a time-based one-time passcode (TOTP) from an authenticator app
        (Google Authenticator, 1Password, Authy, etc.).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Enrolled state */}
      {status?.enabled && !backupCodes && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckIcon sx={{ color: '#10B981' }} />
            <Typography sx={{ fontWeight: 600 }}>Two-factor authentication is enabled</Typography>
            <Chip size="small" label="Active" sx={{ bgcolor: '#10B98120', color: '#10B981', fontWeight: 700 }} />
          </Box>
          {status.enabledAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enabled {new Date(status.enabledAt).toLocaleString()}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Disable 2FA</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter a current code from your authenticator to turn off 2FA.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField size="small" label="6-digit code" value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)} inputProps={{ inputMode: 'numeric', maxLength: 6 }} />
            <Button color="error" variant="outlined" disabled={busy || !disableCode} onClick={disable}>
              {busy ? 'Working…' : 'Disable 2FA'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Backup codes (shown once, right after enabling) */}
      {backupCodes && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckIcon sx={{ color: '#10B981' }} />
            <Typography sx={{ fontWeight: 700 }}>2FA enabled — save your backup codes</Typography>
          </Box>
          <Alert severity="warning" sx={{ my: 2 }}>
            Store these somewhere safe. Each code works once if you lose your authenticator. They won't be shown again.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, fontFamily: 'monospace' }}>
            {backupCodes.map((c) => (
              <Chip key={c} icon={<KeyIcon sx={{ fontSize: 16 }} />} label={c} variant="outlined" sx={{ fontFamily: 'monospace', justifyContent: 'flex-start' }} />
            ))}
          </Box>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => setBackupCodes(null)}>Done</Button>
        </Paper>
      )}

      {/* Not enrolled — enrollment flow */}
      {!status?.enabled && !backupCodes && (
        <Paper sx={{ p: 3 }}>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            <Step><StepLabel>Start</StepLabel></Step>
            <Step><StepLabel>Scan &amp; verify</StepLabel></Step>
            <Step><StepLabel>Backup codes</StepLabel></Step>
          </Stepper>

          {!qr && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You don't have 2FA enabled. Click below to generate a QR code for your authenticator app.
              </Typography>
              <Button variant="contained" startIcon={<SecurityIcon />} disabled={busy} onClick={startEnroll}>
                {busy ? 'Generating…' : 'Enable 2FA'}
              </Button>
            </Box>
          )}

          {qr && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                1. Scan this QR code with your authenticator app:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img src={qr} alt="MFA QR code" style={{ width: 200, height: 200, border: '1px solid #e2e8f0', borderRadius: 8 }} />
              </Box>
              {secret && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                  Or enter this key manually: <code>{secret}</code>
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1 }}>2. Enter the 6-digit code it shows:</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField size="small" label="6-digit code" value={code}
                  onChange={(e) => setCode(e.target.value)} inputProps={{ inputMode: 'numeric', maxLength: 6 }} autoFocus />
                <Button variant="contained" disabled={busy || code.length < 6} onClick={confirmEnroll}>
                  {busy ? 'Verifying…' : 'Verify & enable'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Tip: once your team has enrolled, enforcement can be turned on (ADMIN_MFA_REQUIRED) so a code is
          required for sensitive actions.
        </Typography>
      </Box>
    </Box>
  );
};

export default MfaSettings;
